<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Icon from './Icon.vue'
import ToggleSwitch from './BaseComponents/ToggleSwitch.vue'

const CONSENT_KEY = 'idi-fly-consent'

export interface ConsentPreferences {
  errorTracking: boolean
  performanceMonitoring: boolean
  sessionReplay: boolean
  userIdentification: boolean
  consentDate: string
  consentVersion: string
}

const emit = defineEmits<{
  (e: 'consent-updated', preferences: ConsentPreferences): void
}>()

const isVisible = ref(false)
const showDetails = ref(false)

const preferences = ref<ConsentPreferences>({
  errorTracking: true,
  performanceMonitoring: true,
  sessionReplay: false,
  userIdentification: false,
  consentDate: '',
  consentVersion: '1.0',
})

const CONSENT_VERSION = '1.0'

onMounted(() => {
  const stored = localStorage.getItem(CONSENT_KEY)
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as ConsentPreferences
      // Check if consent version matches, if not show banner again
      if (parsed.consentVersion !== CONSENT_VERSION) {
        isVisible.value = true
      } else {
        preferences.value = parsed
        emit('consent-updated', parsed)
      }
    } catch {
      isVisible.value = true
    }
  } else {
    isVisible.value = true
  }
})

const savePreferences = () => {
  preferences.value.consentDate = new Date().toISOString()
  preferences.value.consentVersion = CONSENT_VERSION
  localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences.value))
  emit('consent-updated', preferences.value)
  isVisible.value = false
}

const acceptAll = () => {
  preferences.value = {
    errorTracking: true,
    performanceMonitoring: true,
    sessionReplay: true,
    userIdentification: true,
    consentDate: new Date().toISOString(),
    consentVersion: CONSENT_VERSION,
  }
  localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences.value))
  emit('consent-updated', preferences.value)
  isVisible.value = false
}

const rejectAll = () => {
  preferences.value = {
    errorTracking: true, // Keep essential error tracking
    performanceMonitoring: false,
    sessionReplay: false,
    userIdentification: false,
    consentDate: new Date().toISOString(),
    consentVersion: CONSENT_VERSION,
  }
  localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences.value))
  emit('consent-updated', preferences.value)
  isVisible.value = false
}

// Expose method to show banner programmatically (e.g., from settings)
const showBanner = () => {
  isVisible.value = true
}

defineExpose({ showBanner })
</script>

<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div v-if="isVisible" class="consent-overlay" data-testid="consent-overlay">
        <div class="consent-banner" data-testid="consent-banner">
          <!-- Header -->
          <div class="consent-header">
            <div class="header-icon">
              <Icon name="shield" :size="20" color="#1b92a6" />
            </div>
            <div class="header-text">
              <h3>Privacy & Monitoring</h3>
              <p>We use monitoring tools to improve your experience</p>
            </div>
          </div>

          <!-- Summary (collapsed) -->
          <div v-if="!showDetails" class="consent-summary">
            <p>
              This application uses error tracking and analytics to help us identify issues and
              improve performance. Session replay may record your interactions for debugging
              purposes.
            </p>
            <button class="link-button" data-testid="consent-customize" @click="showDetails = true">
              Customize preferences
            </button>
          </div>

          <!-- Detailed Options (expanded) -->
          <div v-else class="consent-details">
            <div class="consent-option">
              <div class="option-info">
                <div class="option-header">
                  <span class="option-title">Error Tracking</span>
                  <span class="badge essential">Essential</span>
                </div>
                <p class="option-description">
                  Captures application errors to help us fix issues quickly. Does not collect
                  personal data.
                </p>
              </div>
              <ToggleSwitch
                v-model="preferences.errorTracking"
                label=""
                :showLabel="false"
                disabled
              />
            </div>

            <div class="consent-option">
              <div class="option-info">
                <div class="option-header">
                  <span class="option-title">Performance Monitoring</span>
                </div>
                <p class="option-description">
                  Tracks page load times and app performance to optimize your experience.
                </p>
              </div>
              <ToggleSwitch
                v-model="preferences.performanceMonitoring"
                label=""
                :showLabel="false"
              />
            </div>

            <div class="consent-option">
              <div class="option-info">
                <div class="option-header">
                  <span class="option-title">Session Replay</span>
                  <span class="badge optional">Optional</span>
                </div>
                <p class="option-description">
                  Records anonymized session replays to help us understand and fix issues. Text and
                  sensitive data are masked.
                </p>
              </div>
              <ToggleSwitch v-model="preferences.sessionReplay" label="" :showLabel="false" />
            </div>

            <div class="consent-option">
              <div class="option-info">
                <div class="option-header">
                  <span class="option-title">User Identification</span>
                  <span class="badge optional">Optional</span>
                </div>
                <p class="option-description">
                  Associates error reports with your account to provide better support.
                </p>
              </div>
              <ToggleSwitch
                v-model="preferences.userIdentification"
                label=""
                :showLabel="false"
              />
            </div>

            <button class="link-button" @click="showDetails = false">Show less</button>
          </div>

          <!-- Footer Actions -->
          <div class="consent-footer">
            <button class="btn-secondary" data-testid="consent-reject-all" @click="rejectAll">Reject All</button>
            <div class="footer-right">
              <button v-if="showDetails" class="btn-secondary" data-testid="consent-save-preferences" @click="savePreferences">
                Save Preferences
              </button>
              <button class="btn-primary" data-testid="consent-accept-all" @click="acceptAll">Accept All</button>
            </div>
          </div>

          <!-- Privacy Link -->
          <div class="consent-privacy-link">
            <a href="/privacy-policy" target="_blank">View Privacy Policy</a>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.consent-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding: 1rem;
  background: linear-gradient(to top, rgba(11, 14, 18, 0.9) 0%, transparent 100%);
  pointer-events: none;
}

.consent-banner {
  background: var(--idi-shade0-85, rgba(11, 14, 18, 0.95));
  border: 1px solid var(--idi-shade2-50, rgba(53, 55, 59, 0.5));
  border-radius: 12px;
  max-width: 520px;
  width: 100%;
  padding: 1.25rem;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
  pointer-events: auto;
  backdrop-filter: blur(10px);
}

.consent-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.header-icon {
  background: var(--idi-shade2-50, rgba(53, 55, 59, 0.5));
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text h3 {
  color: var(--txt-primary, #fff);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.header-text p {
  color: var(--txt-secondary-1, #c2c3c4);
  font-size: 0.75rem;
  margin: 0;
}

.consent-summary {
  margin-bottom: 1rem;
}

.consent-summary p {
  color: var(--txt-secondary-1, #c2c3c4);
  font-size: 0.8125rem;
  line-height: 1.5;
  margin: 0 0 0.75rem 0;
}

.link-button {
  background: none;
  border: none;
  color: var(--idi-green, #1b92a6);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.link-button:hover {
  color: var(--idi-green-s1, #0f7b8c);
}

.consent-details {
  margin-bottom: 1rem;
}

.consent-option {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--idi-shade2-25, rgba(53, 55, 59, 0.25));
}

.consent-option:last-of-type {
  border-bottom: none;
}

.option-info {
  flex: 1;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.option-title {
  color: var(--txt-primary, #fff);
  font-size: 0.8125rem;
  font-weight: 500;
}

.badge {
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
}

.badge.essential {
  background: var(--idi-green-a25, rgba(44, 174, 195, 0.25));
  color: var(--idi-green, #1b92a6);
}

.badge.optional {
  background: var(--idi-shade2-50, rgba(53, 55, 59, 0.5));
  color: var(--txt-secondary-1, #c2c3c4);
}

.option-description {
  color: var(--txt-secondary-2, #95989c);
  font-size: 0.6875rem;
  line-height: 1.4;
  margin: 0;
}

.consent-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--idi-shade2-25, rgba(53, 55, 59, 0.25));
}

.footer-right {
  display: flex;
  gap: 0.5rem;
}

.btn-primary {
  background: var(--idi-button, #0f7b8c);
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-primary:hover {
  background: var(--idi-green, #1b92a6);
  transform: scale(0.98);
}

.btn-secondary {
  background: transparent;
  color: var(--txt-secondary-1, #c2c3c4);
  border: 1px solid var(--idi-shade3-50, rgba(96, 99, 102, 0.5));
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
}

.btn-secondary:hover {
  background: var(--idi-shade2-50, rgba(53, 55, 59, 0.5));
  border-color: var(--idi-shade4-50, rgba(149, 152, 156, 0.5));
}

.consent-privacy-link {
  text-align: center;
  margin-top: 0.75rem;
}

.consent-privacy-link a {
  color: var(--txt-secondary-2, #95989c);
  font-size: 0.6875rem;
  text-decoration: none;
}

.consent-privacy-link a:hover {
  color: var(--idi-green, #1b92a6);
  text-decoration: underline;
}

/* Transition */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
